import {CommandLineInterface, consoleLogger, CoreDI, inject, LanguageService} from '@gongxigames/framework/core';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {PopupObject, TemplateUtils} from '@gongxigames/framework/slot/services/TemplateUtils';
import {HTMLSystemPopup} from '@gongxigames/framework/slot/types';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic';
import {RealityCheckTimeState} from '@gongxigames/framework/slot/modules/realitycheck/states/RealityCheckTimeState';
import {
    RealityCheckShowingState
} from '@gongxigames/framework/slot/modules/realitycheck/states/RealityCheckShowingState';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {RealityCheckEvents} from '@gongxigames/framework/slot/modules/realitycheck/RealityCheckEvents';

export class RealityCheckModule extends Module<null, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol.for('RealityCheckModule');
    public gameInFeature = false;

    protected startTime = 0;
    protected timeInGame = 0;
    protected timePassed = 0;

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(CoreDI.CLI)
    private _cli!: CommandLineInterface;

    private _popup!: PopupObject;

    public init(): void {
        /* develblock:start */
        this._cli.addCommand('realityCheck', {
            showPopup: () => {
                this.showPopup();
            },
            close: () => {
                this.eventManager.emit('General.Popups.RemoveAll');
            }
        });
        /* develblock:end */

        if (!this.getTimeout()) {
            consoleLogger.warn('Reality check is off');

            return;
        }

        this.timePassed = this.getTimeoutPassed();

        super.init();
    }

    protected createPopup(): void {
        this._popup = TemplateUtils.createPopup('reality-check-popup', this._languageService.get('realityCheck'));
        this._popup.priority = 1;

        const content = this._popup.content;
        const time = this.getTotalTimeout();

        content.innerHTML = `<p id="reality-check-message">${this._languageService.get('ukPopup1', {
            h: time.hours,
            m: time.minutes
        })}</p>`;

        content.insertAdjacentElement('beforeend', this.createButton(this._languageService.get('continueButton'),
            this.continue.bind(this)));

        if (this.settings.has('historyUrl')) {
            content.insertAdjacentElement('beforeend', this.createButton(this._languageService.get('goAccountButton'),
                this.onGoToAccountButtonClicked.bind(this)));
        }

        content.insertAdjacentElement('beforeend', this.createButton(this._languageService.get('quitButton'),
            this.onQuitGameButtonClicked.bind(this)));

        this._popup.closeButton.hidden = true;
        this._popup.popup.classList.add('long-buttons');
    }

    protected setupEvents(): void {
        this.on('General.Game.STARTED', this.onGameStarted.bind(this));
        this.on(LogicEvents.ROUND_STARTED, this.startRound.bind(this));
        this.on(FlowModuleEvents.FEATURE_STARTED, this.startRound.bind(this));
        this.on(FlowModuleEvents.ROUND_ENDED, this.endRound.bind(this));
        this.onCurrentStateHandle(FlowModuleEvents.FEATURE_FINISHED);
        this.onCurrentStateHandle(FlowModuleEvents.ROUND_ENDED);
    }

    protected startRound(): void {
        this.gameInFeature = true;
    }

    protected endRound(): void {
        this.gameInFeature = false;
    }

    protected onGoToAccountButtonClicked(): void {
        this._eventManager.emit(RealityCheckEvents.GO_TO_ACCOUNT_CLICKED);
        this.continue();
    }

    protected onQuitGameButtonClicked(): void {
        this._eventManager.emit(RealityCheckEvents.QUIT_GAME_CLICKED);
        this.closePopup();
    }

    protected msToTime(duration: number): { hours: number; minutes: number; } {
        return {
            hours: ~~((duration / (1000 * 60 * 60)) % 24),
            minutes: ~~((duration / (1000 * 60)) % 60)
        };
    }

    protected continue(): void {
        this.closePopup();
        this.goToTimeState();
    }

    public showPopup(): void {
        this.updateTimeInGame();
        this.createPopup();
        this.eventManager.emit(RealityCheckEvents.SHOWED);
        this.eventManager.emit('General.Popups.Add', this.getRealityCheckSystemPopup());
    }

    protected closePopup(): void {
        this.eventManager.emit('General.Popups.Remove', this.getRealityCheckSystemPopup());
    }

    protected getRealityCheckSystemPopup(): HTMLSystemPopup {
        return {
            id: 'reality-check-popup',
            priority: this._popup.priority,
            view: this._popup,
            type: 'HTML'
        };
    }

    protected createButton(text: string, callback: (ev: MouseEvent) => any): HTMLElement {
        const button = document.createElement('button');

        button.onclick = callback;
        button.textContent = text;

        return button;
    }

    protected setupStates(): void {
        this.addState(new RealityCheckTimeState(this, 'RealityCheckTimeState'));
        this.addState(new RealityCheckShowingState(this, 'RealityCheckShowingState'));
    }

    protected onGameStarted(): void {
        this.startTime = Date.now() - this.getTimeoutPassed();
        this.goToTimeState();
    }

    public goToTimeState(): void {
        this.gotoState('RealityCheckTimeState');
    }

    public goToShowingState(): void {
        this.gotoState('RealityCheckShowingState');
    }

    protected setupViews(): void {
    }

    protected getTotalTimeout(): { hours: number; minutes: number; } {
        return this.msToTime(this.timeInGame);
    }

    public getTime(): number {
        let time = this.getTimeout();
        const timePassed = this.timePassed && this.takeTimePassed();

        if (timePassed) {
            time = time - (time >= timePassed ? timePassed : timePassed % time);
        }

        return time;
    }

    protected takeTimePassed(): number {
        const timePassed = this.timePassed;

        this.timePassed = 0;

        return timePassed;
    }

    protected getTimeout(): number {
        return this.settings.get('realityCheckTimeout');
    }

    protected getTimeoutPassed(): number {
        if (this.settings.has('realityCheckTimeoutPassed')) {
            return this.settings.get('realityCheckTimeoutPassed');
        }

        return 0;
    }

    protected updateTimeInGame(): void {
        this.timeInGame = Date.now() - this.startTime;
    }
}
